import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import {
  FriendlyTextField,
  FriendlyButton,
  isValidDonorEmail,
  normalizeDonorEmail,
} from "../shared";

/**
 * EmailVerification Component
 * Handles OTP-based email verification for donor identification
 */
const EmailVerification = ({
  t,
  email,
  setEmail,
  otp,
  setOtp,
  otpSent,
  otpVerified,
  isNewDonor,
  donorData,
  onSendOtp,
  onVerifyOtp,
  onChangeEmail = () => {},
  onContinue,
  isMobile = false,
  sendOtpLoading = false,
  verifyOtpLoading = false,
  sendOtpError = null,
  verifyOtpError = null,
}) => {
  const [emailFormatError, setEmailFormatError] = useState(false);
  const normalizedEmail = normalizeDonorEmail(email);
  const emailIsInvalid = !isValidDonorEmail(normalizedEmail);

  const handleEmailChange = (event) => {
    setEmailFormatError(false);
    setEmail(event.target.value);
  };

  const handleSendOtpClick = () => {
    if (emailIsInvalid) {
      setEmailFormatError(true);
      return;
    }

    setEmailFormatError(false);
    onSendOtp();
  };

  // Automatically continue when OTP is verified
  useEffect(() => {
    if (otpVerified && onContinue) {
      // Small delay to show the success message briefly before continuing
      const timer = setTimeout(() => {
        onContinue();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [otpVerified, onContinue]);

  return (
    <Box sx={{ maxWidth: 500, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <EmailIcon sx={{ fontSize: 40, color: "primary.main" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          {t("lets_get_started", "Let's Get Started")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("enter_email_verify", "Enter your email to verify your identity")}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Email Input */}
        <FriendlyTextField
          label={t("email_address", "Email Address")}
          type="email"
          value={email}
          onChange={handleEmailChange}
          disabled={otpSent}
          placeholder="your@email.com"
          isMobile={isMobile}
          error={emailFormatError}
          helperText={
            emailFormatError
              ? t("invalid_email_address", "Please enter a valid email address.")
              : ""
          }
        />

        {/* Send OTP Button */}
        {!otpSent && (
          <FriendlyButton
            onClick={handleSendOtpClick}
            disabled={sendOtpLoading}
            isMobile={isMobile}
          >
            {sendOtpLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("send_verification_code", "Send Verification Code")
            )}
          </FriendlyButton>
        )}

        {/* OTP Input */}
        {otpSent && !otpVerified && (
          <>
            <Alert severity="info" sx={{ py: 1.5 }}>
              {t("otp_sent_check_email", "We've sent a verification code to your email. Please check your inbox.")}
            </Alert>
            <FriendlyTextField
              label={t("verification_code", "Verification Code")}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit code"
              isMobile={isMobile}
            />
            <Stack spacing={2}>
              <FriendlyButton
                fullWidth
                onClick={onVerifyOtp}
                disabled={!otp.trim() || verifyOtpLoading}
                isMobile={isMobile}
              >
                {verifyOtpLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("verify_code", "Verify Code")
                )}
              </FriendlyButton>
              <FriendlyButton
                fullWidth
                variant="outlined"
                onClick={onChangeEmail}
                disabled={verifyOtpLoading}
                isMobile={isMobile}
              >
                {t("change_email", "Change Email")}
              </FriendlyButton>
            </Stack>
          </>
        )}

        {/* Verified State */}
        {otpVerified && (
          <>
            <Alert
              icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
              severity="success"
              sx={{ py: 2 }}
            >
              {isNewDonor
                ? t("email_verified_new", "Email verified! Please complete your registration to continue.")
                : t("welcome_back", `Welcome back${donorData?.first_name ? `, ${donorData.first_name}` : ""}!`)}
            </Alert>
            
            {/* Auto-redirect message */}
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("redirecting", "Redirecting you now...")}
              </Typography>
              <CircularProgress size={20} sx={{ mt: 1 }} />
            </Box>
          </>
        )}

        {/* Error Messages */}
        {(sendOtpError || verifyOtpError) && (
          <Alert severity="error" sx={{ py: 1.5 }}>
            {sendOtpError || verifyOtpError || t("verification_failed", "Verification failed. Please try again.")}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default EmailVerification;
